/**
 * Global Add to Cart Function for Search Spring Integration
 *
 * This function provides a globally accessible way to add products to the cart
 * that triggers the same animations and updates as the native PDP/PLP add to cart.
 */

import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * Global function to add a product to cart
 * @param {Object} options - The options for adding to cart
 * @param {string|number} options.variantId - The variant ID to add
 * @param {number} [options.quantity=1] - The quantity to add
 * @param {Object} [options.properties={}] - Additional line item properties
 * @returns {Promise<Object>} The response from the cart API
 */
const globalAddToCart = async function(options) {
  const {
    variantId,
    quantity = 1,
    properties = {}
  } = options;

  if (!variantId) {
    throw new Error('Variant ID is required');
  }

  // Create form data matching the native add to cart format
  const formData = new FormData();
  formData.append('id', String(variantId));
  formData.append('quantity', String(quantity));

  // Add any custom properties
  Object.entries(properties).forEach(([key, value]) => {
    formData.append(`properties[${key}]`, String(value));
  });

  // Get cart items component section IDs for updating
  const cartItemsComponents = document.querySelectorAll('cart-items-component');
  const cartItemComponentsSectionIds = [];
  cartItemsComponents.forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) {
      cartItemComponentsSectionIds.push(item.dataset.sectionId);
    }
  });

  if (cartItemComponentsSectionIds.length > 0) {
    formData.append('sections', cartItemComponentsSectionIds.join(','));
  }

  const fetchCfg = fetchConfig('javascript', { body: formData });

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    });

    const responseData = await response.json();

    if (responseData.status) {
      // Error occurred
      console.error('Add to cart error:', responseData.message);

      // Dispatch error event
      window.dispatchEvent(new CartErrorEvent('global-add-to-cart', responseData.message));

      // Still dispatch cart add event for max quantity errors
      window.dispatchEvent(
        new CartAddEvent({}, 'global-add-to-cart', {
          didError: true,
          source: 'global-add-to-cart',
          itemCount: quantity,
          productId: null,
        })
      );

      return {
        success: false,
        error: responseData.message,
        data: responseData
      };
    } else {
      // Success - dispatch cart add event to trigger drawer and bubble update
      window.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'product-form-component', // Use same source as native to ensure bubble updates correctly
          itemCount: quantity,
          productId: null,
          sections: responseData.sections,
        })
      );

      // Also dispatch on document to ensure it's caught
      document.dispatchEvent(
        new CartAddEvent({}, String(variantId), {
          source: 'product-form-component',
          itemCount: quantity,
          productId: null,
          sections: responseData.sections,
        })
      );

      // Trigger the add to cart button animation if one exists on the page
      const addToCartButtons = document.querySelectorAll('add-to-cart-component');
      addToCartButtons.forEach(button => {
        if (button.animateAddToCart) {
          button.animateAddToCart();
        }
      });

      // Manually open cart drawer as a fallback
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && cartDrawer.hasAttribute('auto-open')) {
        // Give a small delay to ensure cart updates are processed
        setTimeout(() => {
          if (cartDrawer.open) {
            cartDrawer.open();
          }
        }, 100);
      }

      return {
        success: true,
        data: responseData
      };
    }
  } catch (error) {
    console.error('Add to cart error:', error);

    // Dispatch error event
    window.dispatchEvent(new CartErrorEvent('global-add-to-cart', error.message));

    return {
      success: false,
      error: error.message,
      data: null
    };
  }
};

// Make the function globally available
window.globalAddToCart = globalAddToCart;

/**
 * Alternative function name for convenience
 */
window.addToCart = globalAddToCart;

// Export for ES6 modules if needed
export { globalAddToCart };
export default globalAddToCart;